import { useState, useEffect } from 'react';
import { Camera, Clock, Loader2, X, ZoomIn } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

/**
 * ThumbnailTimeline - Visual proof strip
 * Shows 6 thumbnails from the last hour (every 10 min)
 * Small by default, click/hover to see full size
 */
const ThumbnailTimeline = ({ configId, recordingMode }) => {
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // index of expanded thumb
  const [loaded, setLoaded] = useState(false);

  const fetchThumbnails = async () => {
    if (!configId) return;
    setLoading(true);

    const now = Date.now();
    const timestamps = [];
    // 6 thumbnails: now, -10min, -20min, -30min, -40min, -50min
    for (let i = 0; i < 6; i++) {
      timestamps.push({
        ms: now - (i * 600000), // Every 10 minutes
        label: i === 0 ? 'Now' : i * 10 + 'min ago',
        time: new Date(now - (i * 600000)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });
    }

    const results = [];
    for (const ts of timestamps) {
      const url = `/api/recording-assurance/cameras/${configId}/thumbnail?t=${ts.ms}`;
      try {
        const res = await fetchWithTenant(url);
        if (res.ok && res.status === 200) {
          const blob = await res.blob();
          if (blob.size > 100) {
            results.push({
              url: URL.createObjectURL(blob),
              label: ts.label,
              time: ts.time,
              timestamp: ts.ms,
              available: true,
              size: blob.size
            });
          } else {
            results.push({ label: ts.label, time: ts.time, timestamp: ts.ms, available: false });
          }
        } else {
          results.push({ label: ts.label, time: ts.time, timestamp: ts.ms, available: false });
        }
      } catch (err) {
        results.push({ label: ts.label, time: ts.time, timestamp: ts.ms, available: false, error: err.message });
      }
    }

    setThumbnails(results);
    setLoading(false);
    setLoaded(true);
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      thumbnails.forEach(t => { if (t.url) URL.revokeObjectURL(t.url); });
    };
  }, [thumbnails]);

  if (!loaded) {
    return (
      <div className="ra-card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Camera size={18} style={{ color: '#06b6d4' }} />
          <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>Visual proof (last hour)</h3>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={fetchThumbnails}
            disabled={loading}
          >
            {loading ? <><Loader2 size={14} className="spin" /> Loading thumbnails...</> : <><Camera size={14} /> Load archive snapshots</>}
          </button>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '8px 0 0' }}>
            Pulls 6 snapshots from the VMS archive to visually confirm footage exists
          </p>
        </div>
      </div>
    );
  }

  const availableCount = thumbnails.filter(t => t.available).length;

  return (
    <div className="ra-card" style={{ marginBottom: '20px' }}>
      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Camera size={18} style={{ color: '#06b6d4' }} />
        <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>Visual proof (last hour)</h3>
        <span style={{
          marginLeft: 'auto', fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px',
          background: (() => {
            const isMotion = recordingMode === 'MOTION' || recordingMode === 'SCHEDULED';
            if (availableCount >= 5) return 'rgba(34,197,94,0.12)';
            if (isMotion && availableCount >= 1) return 'rgba(14,165,233,0.12)';
            if (availableCount >= 3) return 'rgba(245,158,11,0.12)';
            return 'rgba(239,68,68,0.12)';
          })(),
          color: (() => {
            const isMotion = recordingMode === 'MOTION' || recordingMode === 'SCHEDULED';
            if (availableCount >= 5) return '#22c55e';
            if (isMotion && availableCount >= 1) return '#0ea5e9';
            if (availableCount >= 3) return '#f59e0b';
            return '#ef4444';
          })()
        }}>
          {availableCount}/6 snapshots retrieved
        </span>
        <button className="btn btn-ghost btn-xs" onClick={fetchThumbnails} disabled={loading} style={{ padding: '4px 8px' }}>
          {loading ? <Loader2 size={12} className="spin" /> : <Camera size={12} />}
        </button>
      </div>

      {/* Thumbnail Strip */}
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
          {thumbnails.map((thumb, i) => (
            <div key={i} style={{ position: 'relative' }}>
              {thumb.available ? (
                <div
                  style={{
                    position: 'relative', cursor: 'pointer', borderRadius: '8px', overflow: 'hidden',
                    border: '2px solid rgba(255,255,255,0.08)', transition: 'all 0.2s',
                    boxShadow: expanded === i ? '0 0 0 2px #06b6d4' : 'none'
                  }}
                  onClick={() => setExpanded(expanded === i ? null : i)}
                >
                  <img
                    src={thumb.url}
                    alt={thumb.label}
                    style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                  />
                  {/* Zoom indicator */}
                  <div style={{
                    position: 'absolute', top: '4px', right: '4px',
                    width: '20px', height: '20px', borderRadius: '4px',
                    background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <ZoomIn size={12} style={{ color: 'var(--text-primary)' }} />
                  </div>
                  {/* Green check overlay */}
                  <div style={{
                    position: 'absolute', bottom: '4px', left: '4px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <span style={{ color: 'var(--text-primary)', fontSize: '10px', fontWeight: 700 }}>&#10003;</span>
                  </div>
                </div>
              ) : (
                <div style={{
                  aspectRatio: '16/9', borderRadius: '8px',
                  background: 'var(--bg-primary)', border: '2px dashed rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <X size={14} style={{ color: 'rgba(255,255,255,0.1)' }} />
                    <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.15)', marginTop: '2px' }}>
                      {(recordingMode === 'MOTION' || recordingMode === 'SCHEDULED') ? 'No motion' : 'No data'}
                    </div>
                  </div>
                </div>
              )}
              {/* Time label */}
              <div style={{ textAlign: 'center', marginTop: '4px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: thumb.available ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {thumb.time}
                </div>
                <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{thumb.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Expanded preview */}
        {expanded !== null && thumbnails[expanded]?.available && (
          <div style={{
            marginTop: '12px', borderRadius: '10px', overflow: 'hidden',
            border: '1px solid rgba(6,182,212,0.3)', position: 'relative'
          }}>
            <img
              src={thumbnails[expanded].url}
              alt={thumbnails[expanded].label}
              style={{ width: '100%', display: 'block', maxHeight: '400px', objectFit: 'contain', background: '#000' }}
            />
            <div style={{
              position: 'absolute', bottom: '0', left: '0', right: '0',
              padding: '8px 14px', background: 'rgba(0,0,0,0.7)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 500 }}>
                {thumbnails[expanded].time} ({thumbnails[expanded].label})
              </span>
              <span style={{ color: '#22c55e', fontSize: '12px' }}>
                Archive footage confirmed
              </span>
            </div>
            <button
              onClick={() => setExpanded(null)}
              style={{
                position: 'absolute', top: '8px', right: '8px',
                width: '28px', height: '28px', borderRadius: '50%',
                background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <X size={14} style={{ color: 'var(--text-primary)' }} />
            </button>
          </div>
        )}

        {/* Summary text */}
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
          {(() => {
            const isMotion = recordingMode === 'MOTION' || recordingMode === 'SCHEDULED';
            if (availableCount === 6) {
              return 'Every snapshot from the last hour was successfully retrieved from the VMS archive. This confirms continuous footage exists and is accessible.';
            }
            if (isMotion && availableCount >= 3) {
              return availableCount + ' of 6 time slots had recorded footage. This is normal for a ' + (recordingMode === 'MOTION' ? 'motion-activated' : 'scheduled') + ' camera. Empty slots indicate periods with no motion detected, which is expected behavior.';
            }
            if (isMotion && availableCount > 0 && availableCount < 3) {
              return availableCount + ' of 6 time slots had recorded footage. This camera is ' + (recordingMode === 'MOTION' ? 'motion-activated' : 'on a recording schedule') + ', so empty slots may simply indicate no activity occurred during those times.';
            }
            if (isMotion && availableCount === 0) {
              return 'No snapshots were retrieved for the last hour. For a ' + (recordingMode === 'MOTION' ? 'motion-activated' : 'scheduled') + ' camera, this could mean no motion was detected. If you expect activity at this time, investigate the camera.';
            }
            if (availableCount >= 3) {
              return availableCount + ' of 6 snapshots were retrieved. Some gaps may exist in the archive for the missing timestamps.';
            }
            if (availableCount > 0) {
              return 'Only ' + availableCount + ' of 6 snapshots could be retrieved. This indicates significant recording gaps in the last hour.';
            }
            return 'No archive snapshots could be retrieved for the last hour. This camera may not be recording or the VMS archive is unavailable.';
          })()}
        </p>
      </div>
    </div>
  );
};

export default ThumbnailTimeline;
